"use client";
import { X } from "lucide-react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import SignColorBlack from "@/assets/sign-color-black.png";
import TapImage from "@/assets/tap.png";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTapHint } from "@/hooks/use-tap-hint";
import { getPaintingData, paintingLabels } from "@/lib/painting-data";
import { cn } from "@/lib/utils";

type DecorativeBoxProps = {
  className?: string;
  signSize?: "sm" | "md" | "lg";
  backgroundColor?: "white" | "gray";
  paintingId?: string;
};

export function DecorativeBox({
  className,
  signSize = "md",
  backgroundColor = "white",
  paintingId,
}: DecorativeBoxProps) {
  const t = useTranslations();
  const [isMobileTooltipOpen, setIsMobileTooltipOpen] = useState(false);
  const [isMobile, setIsMobile] = useState<boolean | null>(null); // null = unknown state for SSR
  const { hasSeenHint, markHintAsSeen } = useTapHint();

  const sizeClasses = {
    sm: "w-14 h-14",
    md: "w-20 h-20",
    lg: "w-24 h-24",
  };

  const bgClasses = {
    white: "bg-white",
    gray: "bg-gray-50",
  };

  const paintingData = getPaintingData(t);
  const labels = paintingLabels(t);
  const paintingInfo = paintingId ? paintingData[paintingId] : null;

  // Detect mobile device using matchMedia for SSR compatibility
  useEffect(() => {
    if (typeof window !== "undefined" && window.matchMedia) {
      const mediaQuery = window.matchMedia("(max-width: 767px)"); // md breakpoint

      // Set initial state
      setIsMobile(mediaQuery.matches);

      // Listen for changes
      const handleChange = (e: MediaQueryListEvent) => {
        setIsMobile(e.matches);
      };

      mediaQuery.addEventListener("change", handleChange);

      return () => {
        mediaQuery.removeEventListener("change", handleChange);
      };
    } else {
      // Fallback for environments without matchMedia
      setIsMobile(false);
    }
  }, []);

  // Reset mobile tooltip when switching to desktop
  useEffect(() => {
    if (isMobile === false) {
      setIsMobileTooltipOpen(false);
    }
  }, [isMobile]);

  const handleMobileClick = () => {
    if (isMobile === true && paintingInfo) {
      // Mark hint as seen globally (this will update all decorative boxes)
      if (!hasSeenHint) {
        markHintAsSeen();
      }

      setIsMobileTooltipOpen(true);
    }
  };

  const closeMobileTooltip = () => {
    setIsMobileTooltipOpen(false);
  };

  // Determine if we should show the tap overlay (only when isMobile is definitively true)
  const shouldShowTapOverlay =
    isMobile === true && paintingInfo && !hasSeenHint;

  const decorativeBox = (
    <div className="relative mx-1">
      <button
        type="button"
        className={cn(
          "border border-gray-200 flex items-center justify-center cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105 min-w-[44px] min-h-[44px] focus:outline-none focus:ring-2 focus:ring-brand-blue focus:ring-offset-2",
          bgClasses[backgroundColor],
          className,
        )}
        onClick={handleMobileClick}
        aria-label={
          paintingInfo
            ? `View details for ${paintingInfo.title} by ${paintingInfo.artist}`
            : "Decorative element"
        }
        title={
          paintingInfo ? `Tap to learn about ${paintingInfo.title}` : undefined
        }
      >
        <Image
          src={SignColorBlack}
          alt="Sign logo"
          width={
            typeof SignColorBlack === "object" && "width" in SignColorBlack
              ? (SignColorBlack as { width: number }).width
              : 80
          }
          height={
            typeof SignColorBlack === "object" && "height" in SignColorBlack
              ? (SignColorBlack as { height: number }).height
              : 80
          }
          className={cn("shadow-xl", sizeClasses[signSize])}
        />
        {/* Tap overlay - only on mobile for first-time users */}
        {shouldShowTapOverlay && (
          <div className="absolute inset-0 bg-white/60 flex items-end justify-end  pointer-events-none">
            <Image
              src={TapImage}
              alt="Tap gesture"
              width={32}
              height={32}
              className="w-9 h-9 animate-scale mr-3 mb-3"
            />
          </div>
        )}
      </button>
    </div>
  );

  if (!paintingInfo) {
    return decorativeBox;
  }

  // Mobile tooltip overlay (only show when isMobile is definitively true)
  if (isMobile === true && isMobileTooltipOpen && paintingInfo) {
    return (
      <>
        {decorativeBox}
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-sm mx-4 relative">
            {/* Close button */}
            <button
              type="button"
              onClick={closeMobileTooltip}
              className="absolute top-3 right-3 p-1 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors duration-200 z-10"
              aria-label="Close tooltip"
            >
              <X size={16} className="text-gray-600" />
            </button>

            <div className="p-4">
              {/* Header */}
              <div className="pb-3 border-b border-gray-200 pr-8">
                <h3 className="font-semibold text-gray-700 text-lg">
                  {paintingInfo.title}
                </h3>
                <p className="text-sm text-gray-600">
                  {paintingInfo.artist}, {paintingInfo.year}
                </p>
              </div>

              {/* Content area */}
              <div className="py-4 space-y-3">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">
                      {labels.medium}:
                    </span>
                    <span className="text-gray-600 text-right flex-1 ml-2">
                      {paintingInfo.medium}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">
                      {labels.dimensions}:
                    </span>
                    <span className="text-gray-600 text-right flex-1 ml-2">
                      {paintingInfo.dimensions}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-700">
                      {labels.location}:
                    </span>
                    <span className="text-gray-600 text-right flex-1 ml-2">
                      {paintingInfo.location}
                    </span>
                  </div>
                </div>

                <div className="pt-2 border-t border-gray-200">
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {paintingInfo.description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Desktop tooltip (disabled on mobile)
  if (isMobile === true) {
    // On mobile, return plain decorative box without desktop tooltip
    return decorativeBox;
  }

  // Desktop tooltip (only when not mobile)
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{decorativeBox}</TooltipTrigger>
        <TooltipContent
          className="w-72 md:w-80 p-0 bg-white border border-gray-200 shadow-2xl overflow-hidden"
          side="top"
          align="center"
          sideOffset={8}
        >
          <div className="relative px-4 py-2">
            {/* Header with gradient background */}
            <div className="py-3 border-b border-gray-200">
              <h3 className="font-semibold text-gray-700 text-lg">
                {paintingInfo.title}
              </h3>
              <p className="text-sm text-gray-600">
                {paintingInfo.artist}, {paintingInfo.year}
              </p>
            </div>

            {/* Content area */}
            <div className="py-4 space-y-3">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700">
                    {labels.medium}:
                  </span>
                  <span className="text-gray-600 text-right flex-1 ml-2">
                    {paintingInfo.medium}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700">
                    {labels.dimensions}:
                  </span>
                  <span className="text-gray-600 text-right flex-1 ml-2">
                    {paintingInfo.dimensions}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700">
                    {labels.location}:
                  </span>
                  <span className="text-gray-600 text-right flex-1 ml-2">
                    {paintingInfo.location}
                  </span>
                </div>
              </div>

              <div className="pt-2 border-t border-gray-200">
                <p className="text-sm text-gray-600 leading-relaxed">
                  {paintingInfo.description}
                </p>
              </div>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
